<template>
  <div class="mt-6">
    <v-row justify="center">
      <v-col cols="12" md="6">
        <v-card class="pt-8 pb-6 white rounded-xl">
          <div class="center-logo">
            <router-link to="/">
              <img class="logo-size" :src="domainLogo" alt="" />
            </router-link>
          </div>
          <v-toolbar
            v-if="recording.title"
            class="font-weight-bold text-h6 mt-n5 ml-4 d-flex justify-center"
            flat
          >
            {{ recording.title }}
          </v-toolbar>
          <div style="position: relative" class="video-frame">
            <div
              v-if="interaction === 'email_capture' && showNow"
              class="white--text interaction-container"
            >
              <h3 class="mb-4">
                {{
                  video_settings["upper_text"] ||
                  "Enter your email address to watch this video"
                }}
              </h3>
              <v-row class="text-left">
                <v-col
                  v-if="video_settings['form_fields']['name'] === 'yes'"
                  cols="12"
                  md="6"
                >
                  <label>First Name</label>
                  <v-text-field
                    class="py-0"
                    v-model="form['first_name']"
                    dense
                    outlined
                    background-color="white"
                    type="text"
                    style="height: 44px"
                  ></v-text-field>
                </v-col>
                <v-col
                  v-if="video_settings['form_fields']['name'] === 'yes'"
                  cols="12"
                  md="6"
                >
                  <label>Last Name</label>
                  <v-text-field
                    class="py-0"
                    v-model="form['last_name']"
                    dense
                    outlined
                    background-color="white"
                    type="text"
                    style="height: 44px"
                  ></v-text-field>
                </v-col>
                <v-col
                  v-if="video_settings['form_fields']['email'] === 'yes'"
                  cols="12 my-0 py-0 mb-4"
                >
                  <label>Email Address</label>
                  <v-text-field
                    class="py-0"
                    v-model="form.email"
                    dense
                    outlined
                    background-color="white"
                    type="email"
                    style="height: 44px"
                  ></v-text-field>
                </v-col>
                <v-row>
                  <v-col cols="12 text-center my-0 py-0">
                    <v-btn
                      class="capitalize primary rounded-xl"
                      :loading="loading"
                      @click="playVideo"
                    >
                      {{
                        recording.video_settings.email_capture_button_text ||
                        "Play video"
                      }}
                    </v-btn>
                  </v-col>
                  <v-col
                    v-if="video_settings['lower_text'].length"
                    cols="10 text-center pt-1"
                    offset="1"
                  >
                    <div>{{ lowerText }}</div>
                  </v-col>
                  <v-col cols="12">
                    <v-btn
                      v-if="video_settings['allow_skip'] === 'yes'"
                      class="white--text capitalize font-weight-bold"
                      text
                      style="position: absolute; bottom: 0; right: 35px"
                      @click="skip"
                    >
                      Skip
                    </v-btn>
                  </v-col>
                </v-row>
              </v-row>
            </div>
            <div
              v-if="interaction === 'call_to_action' && showNow"
              class="white--text interaction-container"
            >
              <v-row style="position: relative; height: 100%">
                <v-spacer></v-spacer>
                <v-col class="download-btn">
                  <v-btn
                    text
                    color="white"
                    class="capitalize font-weight-bold text-h4"
                    :loading="loading"
                    download
                    @click="openLink(recording)"
                  >
                    {{
                      recording.video_settings.cta_button_text ||
                      "Download Now!"
                    }}
                  </v-btn>
                  <v-spacer></v-spacer>
                </v-col>
                <v-btn
                  v-if="video_settings['allow_skip'] === 'yes'"
                  class="white--text capitalize font-weight-bold"
                  text
                  style="position: absolute; bottom: 25px; right: 35px"
                  @click="skip"
                >
                  Skip
                </v-btn>
              </v-row>
            </div>
            <div class="video-container">
              <div class="position-relative">
                <!-- Video Skeleton Loader -->
                <div v-if="isVideoLoading" class="video-skeleton">
                  <div class="skeleton-overlay">
                    <v-progress-circular
                      indeterminate
                      size="64"
                      color="primary"
                      class="mb-4"
                    ></v-progress-circular>
                    <div class="text-h6 white--text">Loading video...</div>
                    <div class="text-caption white--text">
                      Please wait while we prepare your video
                    </div>
                  </div>
                </div>

                <!-- Video Error State -->
                <div v-if="videoLoadError" class="video-error-container">
                  <div class="error-content text-center pa-8">
                    <v-icon size="64" color="error" class="mb-4"
                      >mdi-alert-circle</v-icon
                    >
                    <div class="text-h6 mb-2">Video Failed to Load</div>
                    <div class="text-body-2 grey--text mb-4">
                      There was an error loading this video. Please check your
                      connection and try again.
                    </div>
                    <v-btn
                      color="primary"
                      @click="retryVideoLoad"
                      class="rounded-lg"
                    >
                      <v-icon left>mdi-refresh</v-icon>
                      Retry
                    </v-btn>
                  </div>
                </div>

                <!-- Actual Video - Always rendered but hidden when loading -->
                <video
                  class="plyr"
                  :class="{ 'video-hidden': isVideoLoading || videoLoadError }"
                  ref="video"
                  id="video"
                  playsinline
                  controls
                  controlsList="nodownload"
                  oncontextmenu="return false"
                  preload="metadata"
                  @loadstart="onVideoLoadStart"
                  @loadeddata="onVideoLoaded"
                  @canplay="onVideoCanPlay"
                  @error="onVideoError"
                  @timeupdate="checkView"
                ></video>
              </div>
            </div>
            <v-row class="mt-4 mb-2">
              <v-col cols="6" md="6" lg="6">
                <div class="recording-stats mb-4">
                  <!-- Analytics Stats -->
                  <template
                    v-if="
                      recording.video_settings &&
                      recording.video_settings.allow_analytics_view === 'yes'
                    "
                  >
                    <span class="stat-item">
                      <v-icon size="16" color="grey">mdi-eye</v-icon>
                      {{ recording.total_views || 0 }}
                    </span>
                    <span class="stat-item">
                      <v-icon size="16" color="grey">mdi-comment</v-icon>
                      {{ recording.comments_count || 0 }}
                    </span>
                  </template>

                  <!-- Download icon - separate from analytics -->
                  <template
                    v-if="
                      recording.video_settings &&
                      (recording.video_settings.allow_download === 'yes' ||
                        recording.video_settings.allow_download === null)
                    "
                  >
                    <span
                      class="stat-item cta-download-btn"
                      @click="downloadVideo"
                      title="Download video"
                    >
                      <v-icon size="18">mdi-download</v-icon>
                    </span>
                  </template>
                </div>
              </v-col>
              <v-col cols="6" md="6" lg="6" class="cta-btn-container">
                <template
                  v-if="
                    recording.video_settings &&
                    recording.video_settings.cta_button_text &&
                    recording.video_settings.allow_cta_display === 'yes'
                  "
                >
                  <a
                    :href="recording.video_settings.cta_button_url"
                    class="cta-btn"
                    :style="ctaButtonStyles"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {{ recording.video_settings.cta_button_text }}
                  </a>
                </template>
              </v-col>
            </v-row>
          </div>
          <v-card-text
            v-if="recording.message"
            class="font-weight-bold message-container"
          >
            {{ recording.message }}
          </v-card-text>

          <!-- Comment Container -->
          <v-card-actions
            v-if="
              recording.video_settings &&
              recording.video_settings.allow_comments === 'yes'
            "
            class="pa-10"
          >
            <!-- Comments section -->
            <div class="comments-container" style="width: 100%">
              <!-- Comments toggle header -->
              <p
                class="comments-toggle"
                @click="toggleComments"
                style="
                  cursor: pointer;
                  user-select: none;
                  margin-bottom: 16px;
                  font-weight: 500;
                  color: #1877f2;
                "
              >
                {{ showComments ? "Hide all comments" : "View all comments" }}
                <v-icon
                  size="16"
                  :style="{
                    transform: showComments ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.3s ease',
                  }"
                >
                  mdi-chevron-down
                </v-icon>
              </p>

              <!-- Collapsible comments content - ONLY the comments list -->
              <div v-show="showComments" class="comments-content">
                <!-- Comments list -->
                <div class="comments-list">
                  <div
                    v-for="comment in comments"
                    :key="comment.id"
                    class="comment-wrapper"
                  >
                    <!-- Main comment -->
                    <div
                      class="comment-item"
                      @mouseenter="hoveredComment = comment.id"
                      @mouseleave="hoveredComment = null"
                    >
                      <div class="comment-content">
                        <div class="comment-left">
                          <v-avatar size="32" class="comment-avatar">
                            <v-icon size="20" color="grey darken-1"
                              >mdi-account</v-icon
                            >
                          </v-avatar>

                          <div class="comment-body">
                            <div class="comment-header">
                              <span class="username">{{
                                comment.username
                              }}</span>
                              <span class="timestamp"
                                >·&nbsp;{{ timeAgo(comment.created_at) }}</span
                              >
                            </div>

                            <div
                              class="comment-text"
                              v-if="comment.content.trim()"
                            >
                              {{ comment.content }}
                            </div>

                            <div class="comment-actions">
                              <button
                                class="action-btn reply-btn"
                                @click="toggleReplyForm(comment.id)"
                              >
                                Reply
                              </button>

                              <button
                                class="action-btn emoji-btn"
                                title="Coming soon..."
                              >
                                <v-icon size="16"
                                  >mdi-emoticon-happy-outline</v-icon
                                >
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Reply form -->
                    <div v-if="showReplyForm === comment.id" class="reply-form">
                      <div class="reply-form-content">
                        <v-avatar size="28" class="reply-avatar">
                          <v-icon size="16" color="grey darken-1"
                            >mdi-account</v-icon
                          >
                        </v-avatar>

                        <div class="reply-input-wrapper">
                          <div class="reply-input-container">
                            <textarea
                              :ref="'replyInput-' + comment.id"
                              v-model="replyText"
                              placeholder="Leave a comment"
                              rows="1"
                              class="reply-textarea"
                              @input="autoResize"
                              @keydown.enter.prevent="addReply(comment.id)"
                            ></textarea>
                          </div>

                          <div class="reply-form-actions">
                            <button
                              class="form-btn cancel-btn"
                              @click="cancelReply"
                            >
                              Cancel
                            </button>
                            <button
                              class="form-btn reply-submit-btn"
                              @click="addReply(comment.id)"
                              :disabled="!replyText.trim()"
                            >
                              Reply
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>

                    <!-- Replies -->
                    <div
                      v-if="comment.replies && comment.replies.length > 0"
                      class="replies-container"
                    >
                      <div
                        v-for="reply in comment.replies"
                        :key="reply.id"
                        class="reply-item"
                        @mouseenter="hoveredComment = reply.id"
                        @mouseleave="hoveredComment = null"
                      >
                        <div class="reply-content">
                          <div class="reply-left">
                            <v-avatar size="28" class="reply-avatar">
                              <v-icon size="16" color="grey darken-1"
                                >mdi-account</v-icon
                              >
                            </v-avatar>

                            <div class="reply-body">
                              <div class="reply-header">
                                <span class="username">{{
                                  reply.username
                                }}</span>
                                <span class="timestamp"
                                  >·&nbsp;{{ timeAgo(reply.created_at) }}</span
                                >
                              </div>

                              <div
                                class="reply-text"
                                v-if="reply.content.trim()"
                              >
                                {{ reply.content }}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Add new comment form - OUTSIDE collapsible -->
              <div class="new-comment-form" v-if="showNewCommentForm">
                <div class="new-comment-content">
                  <div class="new-comment-input-wrapper">
                    <v-textarea
                      solo
                      outlined
                      rows="1"
                      class="new-comment-textarea"
                      label="Leave a comment..."
                      ref="newCommentInput"
                      v-model="newCommentText"
                      :rules="requiredRules"
                      style="border-radius: 8px"
                      @input="autoResize($event)"
                      @keydown.enter.prevent="addComment"
                    ></v-textarea>

                    <div class="new-comment-form-actions mt-2">
                      <button
                        class="form-btn cancel-btn"
                        @click="cancelNewComment"
                      >
                        Cancel
                      </button>
                      <button
                        class="form-btn reply-submit-btn"
                        @click="addComment"
                        :loading="loading"
                        :disabled="!newCommentText.trim()"
                      >
                        Comment
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Add comment button - OUTSIDE collapsible -->
              <div class="add-comment-trigger mt-4" v-if="!showNewCommentForm">
                <button
                  class="add-comment-btn"
                  @click="showNewCommentForm = true"
                >
                  <v-avatar size="32" class="mr-2">
                    <v-icon size="20" color="grey darken-1">mdi-account</v-icon>
                  </v-avatar>
                  <span class="add-comment-text">Leave a comment</span>
                </button>
              </div>
            </div>

            <!--            <v-spacer></v-spacer>-->
            <!--            <v-btn-->
            <!--              to="/sign_in"-->
            <!--              class="secondary black&#45;&#45;text capitalize rounded-xl px-10"-->
            <!--              large-->
            <!--              text-->
            <!--            >-->
            <!--              Login-->
            <!--            </v-btn>-->
            <!--            <v-btn-->
            <!--              to="/sign_up"-->
            <!--              class="primary capitalize rounded-xl ml-6 px-10"-->
            <!--              large-->
            <!--              text-->
            <!--            >-->
            <!--              Register-->
            <!--            </v-btn>-->
            <!--            <v-spacer></v-spacer>-->
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>
    <!-- Identity Modal -->
    <v-dialog v-model="showIdentityForm" max-width="400" persistent>
      <v-card class="rounded-sm">
        <v-card-title class="text-h6">
          How would you like to comment?
        </v-card-title>

        <v-card-text>
          <v-radio-group v-model="commentIdentity.isAnonymous">
            <v-radio :value="false" label="Comment with my name"></v-radio>
            <v-radio :value="true" label="Comment anonymously"></v-radio>
          </v-radio-group>

          <div v-if="!commentIdentity.isAnonymous">
            <v-text-field
              v-model="commentIdentity.name"
              label="Your name"
              required
              outlined
              dense
              class="mb-2"
            ></v-text-field>

            <v-text-field
              v-model="commentIdentity.email"
              label="Your email (optional)"
              type="email"
              outlined
              dense
            ></v-text-field>
          </div>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="cancelIdentity">Cancel</v-btn>
          <v-btn
            color="primary"
            @click="submitIdentity"
            :disabled="
              !commentIdentity.isAnonymous && !commentIdentity.name.trim()
            "
          >
            Continue
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapActions } from "vuex";
import Plyr from "plyr";
import "plyr/dist/plyr.css";
export default {
  name: "ShareRecording",
  components: {},
  data() {
    return {
      player: null,
      recording: {},
      interaction: "",
      form: {},
      loading: false,
      video_settings: {},
      interval: null,
      duration: 0,
      currentTime: 0,
      showNow: false,
      timer: "",
      domainLogo: null,
      hasCountedView: false,

      // Video loading states
      isVideoLoading: true,
      videoLoadError: false,
      retryCount: 0,
      maxRetries: 3,

      newCommentText: "",
      replyText: "",
      showNewCommentForm: false,
      showReplyForm: null,
      requiredRules: [(v) => !!v || "Comment cannot be empty"],
      comments: [],
      commentIdentity: {
        name: "",
        email: "",
        isAnonymous: false,
      },
      showIdentityForm: false,
      pendingComment: null,
      pendingReply: null,
      showComments: false,
    };
  },
  computed: {
    lowerText() {
      let text = this.video_settings["lower_text"];
      if (text.length > 70) {
        text = text.substr(0, 70) + "...";
      }
      return text;
    },
    ctaButtonStyles() {
      const videoSettings = this.recording?.video_settings;
      if (!videoSettings) return {};

      const styles = {
        backgroundColor: videoSettings.cta_button_color || "#000000",
        color: videoSettings.cta_text_color || "#ffffff",
        border: `1px solid ${videoSettings.cta_button_color || "#999999"}`,
      };

      // Apply border radius based on button style
      switch (videoSettings.cta_button_style) {
        case "rounded":
          styles.borderRadius = "25px";
          break;
        case "squared":
          styles.borderRadius = "4px";
          break;
        case "srounded":
          styles.borderRadius = "8px";
          break;
        default:
          styles.borderRadius = "25px";
      }

      return styles;
    },
  },
  async created() {
    await this.getVideoUrl();
  },
  mounted() {
    this.getVideoComments();
  },
  methods: {
    ...mapActions({
      add_contact: "contacts/add_contact",
      video_public_comments: "comments/video_public_comments",
      add_public_comment: "comments/add_public_comment",
    }),

    // Video loading methods
    initPlayer() {
      this.$nextTick(() => {
        if (this.player) {
          this.player.destroy();
        }

        // Don't initialize player until video is loaded
        if (!this.isVideoLoading && !this.videoLoadError && this.$refs.video) {
          this.player = new Plyr(this.$refs.video, {
            controls: [
              "play-large",
              "restart",
              "play",
              "progress",
              "current-time",
              "duration",
              "mute",
              "volume",
              "captions",
              "settings",
              "airplay",
              "pip",
              "fullscreen",
            ],
            settings: ["captions", "speed", "quality"],
            autoplay: false,
            keyboard: { focused: true, global: true },
            fullscreen: { enabled: true, fallback: true, iosNative: true },
          });

          // Add the play event listener after player is initialized
          this.$refs.video.addEventListener("play", () => {
            this.play();
          });
        }
      });
    },

    onVideoLoadStart() {
      console.log("Video loading started");
      this.isVideoLoading = true;
      this.videoLoadError = false;
    },

    onVideoLoaded() {
      console.log("Video data loaded");
      // Video metadata is loaded, but might still be buffering
    },

    onVideoCanPlay() {
      console.log("Video can start playing");
      this.isVideoLoading = false;
      this.videoLoadError = false;
      this.retryCount = 0;

      // Initialize player after video is ready
      setTimeout(() => {
        this.initPlayer();
      }, 100);
    },

    onVideoError(event) {
      console.error("Video loading error:", event);
      this.isVideoLoading = false;
      this.videoLoadError = true;

      // Auto-retry on first few failures
      if (this.retryCount < this.maxRetries) {
        setTimeout(() => {
          this.retryVideoLoad();
        }, 2000);
      }
    },

    retryVideoLoad() {
      if (this.retryCount < this.maxRetries) {
        this.retryCount++;
        console.log(`Retrying video load (attempt ${this.retryCount})`);

        this.isVideoLoading = true;
        this.videoLoadError = false;

        // Force video reload
        this.$nextTick(() => {
          if (this.$refs.video && this.recording.video_url) {
            this.$refs.video.src = this.recording.video_url;
            this.$refs.video.load();
          }
        });
      } else {
        console.error("Maximum retry attempts reached");
      }
    },

    async getVideoUrl() {
      try {
        const response = await this.$axios.get(
          "/recordings/play-video/" + this.$route.params.id
        );
        this.recording = response.data;
        const customDomainsDtls = response.data.user["custom_domains"];

        // default domain config
        const defaultDomain = customDomainsDtls.find(
          (domain) => domain.default === "yes"
        );
        if (defaultDomain) {
          this.domainLogo = defaultDomain.domain_logo_url;
          localStorage.setItem("favicon", defaultDomain.favicon_url);
          localStorage.setItem("tagline", defaultDomain.tagline);
          localStorage.setItem("domain_logo", defaultDomain.domain_logo_url);
          let checkDefault = defaultDomain.tagline !== undefined ? true : false;
          localStorage.setItem("is_custom_domain", checkDefault);
        } else {
          this.domainLogo = require("../../assets/logo.png");
          localStorage.removeItem("favicon");
          localStorage.removeItem("tagline");
          localStorage.removeItem("domain_logo");
          localStorage.setItem("is_custom_domain", false);
        }

        this.logo = response.data?.user?.settings?.logo_url;
        if (response.data.video_settings !== null) {
          this.video_settings = response.data.video_settings;
          this.interaction = this.video_settings["interaction_type"];
          this.emailCaptureInteraction();
          if (this.video_settings.timer) {
            const time = this.video_settings.timer;
            const a = time.split(":");
            this.timer = +a[0] * 60 * 60 + +a[1] * 60 + +a[2];
          }
        }

        // Set video source directly - video element is always rendered
        if (response.data.video_url) {
          this.$nextTick(() => {
            if (this.$refs.video) {
              this.$refs.video.src = response.data.video_url;
            }
          });
        }
      } catch (e) {
        console.error("Error fetching video data:", e);
        this.$toast.error("Error fetching video, please try again");
        this.isVideoLoading = false;
        this.videoLoadError = true;
      }
    },

    async getVideoComments() {
      try {
        const response = await this.video_public_comments(
          this.$route.params.id
        );
        if (response.success) {
          this.comments = response.data;
        }
      } catch (e) {
        console.log(e);
        this.comments = [];
      }
    },

    emailCaptureInteraction() {
      if (this.video_settings["appearance"] === "before_video") {
        this.showNow = true;
      }
    },
    play() {
      if (this.video_settings["appearance"] === "before_video") {
        this.showNow = true;
      }
      this.$refs.video.play();
      this.interval = setInterval(() => {
        this.currentTime = this.$refs.video.currentTime;
        this.duration = this.$refs.video.duration;
        if (
          this.video_settings["appearance"] === "during_video" &&
          this.currentTime >= this.timer
        ) {
          if (!this.showNow) {
            this.showNow = true;
            this.$refs.video.pause();
            clearInterval(this.interval);
          }
        }
        if (this.currentTime >= this.duration) {
          if (this.video_settings["appearance"] === "after_video") {
            this.showNow = true;
          }
          clearInterval(this.interval);
        }
      }, 1000);
    },
    skip() {
      this.interaction = "skip";
      this.play();
    },
    async playVideo() {
      this.loading = true;
      this.form.user_id = this.recording.user_id;
      try {
        const response = await this.add_contact(this.form);
        this.loading = false;
        this.$response.sendSuccess(response);
        this.interaction = "play_video";
        setTimeout(() => {
          this.play();
        }, 500);
      } catch (e) {
        this.$response.sendError(e);
        this.loading = false;
      }
    },
    async openLink(recording) {
      const video_settings = recording.video_settings;
      if (video_settings["open_link_in_new_tab"]) {
        window.open(video_settings["redirect_to"]);
      } else {
        window.location.href = video_settings["redirect_to"];
      }
      this.interaction = video_settings["cta_button_text"];
      this.play();
    },

    // Implementing view count increment when 10% of video is watched
    checkView(event) {
      if (this.hasCountedView) return;

      const video = event.target;
      const duration = video.duration;
      const currentTime = video.currentTime;

      if (duration > 0 && currentTime >= duration * 0.1) {
        this.incrementView();
        this.hasCountedView = true;
      }
    },
    async incrementView() {
      try {
        const response = await this.$axios.post(
          "/recordings/" + this.$route.params.id + "/public-view"
        );

        if (response.data) {
          // Ensure the data has required properties before committing
          const updatedData = {
            ...this.recording,
            ...response.data,
            category: response.data.category || this.recording.category,
            user: response.data.user || this.recording.user,
          };

          this.$store.commit("recordings/SHOW_DETAILS", updatedData);
          console.log("Updated total_views:", updatedData.total_views);
        }
      } catch (error) {
        console.error("Error incrementing view:", error);
      }
    },
    async downloadVideo() {
      try {
        // Create a temporary anchor element to trigger download
        const link = document.createElement("a");
        link.href = this.recording.video_url;
        link.download = this.recording.title || "video";

        // For videos hosted on same domain
        if (
          this.recording.video_url.startsWith("/") ||
          this.recording.video_url.includes(window.location.origin)
        ) {
          link.click();
        } else {
          // For external URLs, open in new tab (browser will handle download)
          window.open(this.recording.video_url, "_blank");
        }
      } catch (error) {
        console.error("Download error:", error);
      }
    },
    cancelNewComment() {
      this.newCommentText = "";
      this.showNewCommentForm = false;
    },

    async addComment() {
      if (!this.newCommentText.trim()) return;

      // Check if we need to ask for identity
      if (!this.commentIdentity.name && !this.commentIdentity.isAnonymous) {
        this.pendingComment = {
          content: this.newCommentText.trim(),
          type: "comment",
        };
        this.showIdentityForm = true;
        return;
      }

      this.loading = true;

      const newComment = {
        user_id: this.recording.user.id,
        recording_id: this.recording.id,
        username: this.getDisplayName(),
        content: this.newCommentText.trim(),
        created_at: new Date(),
        replies: [],
      };

      try {
        const response = await this.saveComment(newComment);

        if (response && response.data) {
          const commentFromBackend = {
            id: response.data.id,
            user_id: response.data.user_id,
            parent_id: response.data.parent_id,
            recording_id: response.data.recording_id,
            username: this.getDisplayName(),
            content: response.data.content,
            created_at: response.data.created_at,
            replies: [],
          };

          this.comments.unshift(commentFromBackend);
          console.log("Comment added successfully");
        }
      } catch (error) {
        console.error("Error adding comment:", error);
      } finally {
        this.newCommentText = "";
        this.showNewCommentForm = false;
        this.loading = false;
      }
    },

    async addReply(parentId) {
      if (!this.replyText.trim()) return;

      // Check if we need to ask for identity
      if (!this.commentIdentity.name && !this.commentIdentity.isAnonymous) {
        this.pendingReply = {
          content: this.replyText.trim(),
          parentId: parentId,
          type: "reply",
        };
        this.showIdentityForm = true;
        return;
      }

      this.loading = true;

      const newReply = {
        parent_id: parentId,
        user_id: this.recording.user.id,
        recording_id: this.recording.id,
        username: this.getDisplayName(),
        content: this.replyText.trim(),
        created_at: new Date(),
      };

      try {
        const response = await this.saveComment(newReply);

        if (response && response.data) {
          const replyFromBackend = {
            id: response.data.id,
            parent_id: response.data.parent_id,
            user_id: response.data.user_id,
            recording_id: response.data.recording_id,
            username: this.getDisplayName(),
            content: response.data.content,
            created_at: response.data.created_at,
          };

          const parentComment = this.comments.find((c) => c.id === parentId);
          if (parentComment) {
            if (!parentComment.replies) {
              this.$set(parentComment, "replies", []);
            }
            parentComment.replies.push(replyFromBackend);
          }

          console.log("Reply added successfully");
        }
      } catch (error) {
        console.error("Error adding reply:", error);
      } finally {
        this.replyText = "";
        this.showReplyForm = null;
        this.loading = false;
      }
    },

    async saveComment(comment) {
      this.form = {
        user_id: comment.user_id,
        parent_id: comment.parent_id,
        recording_id: comment.recording_id,
        comment: comment.content,
        commented_at: 0,
        commenter_name: this.commentIdentity.isAnonymous
          ? null
          : this.commentIdentity.name,
        commenter_email: this.commentIdentity.isAnonymous
          ? null
          : this.commentIdentity.email,
        is_anonymous: this.commentIdentity.isAnonymous,
      };

      try {
        const response = await this.add_public_comment(this.form);
        console.log("Comment saved:", response.data);
        return response;
      } catch (error) {
        console.error("Error saving comment:", error);
        throw error;
      }
    },

    getDisplayName() {
      if (this.commentIdentity.isAnonymous) {
        return "Anonymous";
      }
      return this.commentIdentity.name || "Anonymous";
    },

    submitIdentity() {
      if (
        !this.commentIdentity.isAnonymous &&
        !this.commentIdentity.name.trim()
      ) {
        return;
      }

      this.showIdentityForm = false;

      // Process the pending comment or reply
      if (this.pendingComment) {
        this.newCommentText = this.pendingComment.content;
        this.pendingComment = null;
        this.addComment();
      } else if (this.pendingReply) {
        this.replyText = this.pendingReply.content;
        this.addReply(this.pendingReply.parentId);
        this.pendingReply = null;
      }
    },

    cancelIdentity() {
      this.showIdentityForm = false;
      this.pendingComment = null;
      this.pendingReply = null;
      this.commentIdentity = {
        name: "",
        email: "",
        isAnonymous: false,
      };
    },

    timeAgo(dateString) {
      const date = new Date(dateString);
      const seconds = Math.floor((new Date() - date) / 1000);

      if (seconds < 60) return `${seconds} sec ago`;
      const minutes = Math.floor(seconds / 60);
      if (minutes < 60) return `${minutes} min ago`;
      const hours = Math.floor(minutes / 60);
      if (hours < 24) return `${hours} hr ago`;
      const days = Math.floor(hours / 24);
      if (days < 30) return `${days} day${days > 1 ? "s" : ""} ago`;
      const months = Math.floor(days / 30);
      if (months < 12) return `${months} month${months > 1 ? "s" : ""} ago`;
      const years = Math.floor(months / 12);
      return `${years} year${years > 1 ? "s" : ""} ago`;
    },

    autoResize(event) {
      // Handle v-textarea (Vuetify) input
      if (event.target === undefined && typeof event === "string") {
        const textarea =
          this.$refs.newCommentInput.$el.querySelector("textarea");
        if (textarea) {
          textarea.style.height = "auto";
          textarea.style.height = `${textarea.scrollHeight}px`;
        }
        return;
      }

      // Handle regular textarea input
      const textarea = event.target;
      if (textarea) {
        textarea.style.height = "auto";
        textarea.style.height = `${textarea.scrollHeight}px`;
      }
    },
    toggleReplyForm(commentId) {
      if (this.showReplyForm === commentId) {
        this.showReplyForm = null;
        this.replyText = "";
      } else {
        this.showReplyForm = commentId;
        this.replyText = "";
        this.$nextTick(() => {
          const replyInput = this.$refs[`replyInput-${commentId}`];
          if (replyInput && replyInput[0]) {
            replyInput[0].focus();
          }
        });
      }
    },
    cancelReply() {
      this.showReplyForm = null;
      this.replyText = "";
      this.editingCommentId = null;
      this.originalCommentText = "";
    },
    toggleComments() {
      this.showComments = !this.showComments;
    },
  },
  beforeUnmount() {
    if (this.player) {
      this.player.destroy();
    }
    if (this.interval) {
      clearInterval(this.interval);
    }
  },
  // beforeCreate() {
  //   this.getVideoUrl();
  // },
};
</script>

<style lang="scss" scoped>
.comments-container {
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  // max-width: 600px;
  margin: 0;
  padding: 0;
  text-align: left;
}
.interaction-container {
  background: rgba(0, 0, 0, 0.7);
  position: absolute;
  width: 100% !important;
  bottom: 0px;
  height: 53vh !important;
  left: 0;
  right: 0;
  margin-left: auto;
  margin-right: auto;
  z-index: 99999999999;
  padding: 10px 40px;
}
.download-btn {
  position: absolute;
  top: 45%;
  color: #ffffff !important;
}
.video-frame {
  margin: 0px 40px !important;
}
.video-container {
  width: 100%;
  overflow: hidden;
  border: 2px solid rgb(0, 0, 0);
  border-radius: 10px !important;
  background-color: transparent !important;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  video {
    background-color: #000;
  }
}
.video-content video {
  width: 100%;
  background-color: #222 !important;
  border-radius: 10px !important;
}
.message-container {
  margin-top: 10px !important;
  text-align: left !important;
  padding: 0px 40px !important;
}
.logo-size {
  height: 50px;
  width: 150px;
  margin-bottom: 20px;
}

.recording-stats {
  display: flex;
  gap: 16px;
}
.stat-item {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #666;
}

.cta-btn-container {
  // border: 1px solid red;
  padding-right: 20px;
}
.cta-btn {
  min-width: 100px;
  text-align: center;
  float: right !important;
  padding: 6px 40px;
  text-decoration: none !important;
  // display: inline-block;
  transition: opacity 0.3s ease;

  &:hover {
    opacity: 0.8;
  }
}

.cta-download-btn {
  cursor: pointer;
  &:hover {
    opacity: 0.8;
  }
}

// Add comment trigger
.comment-wrapper {
  margin-bottom: 8px;
}

.comment-item {
  position: relative;
  padding: 8px 4px;
  border-radius: 6px;

  &:hover {
    background-color: #f2f3f5;
    .comment-actions-hover {
      opacity: 1;
      visibility: visible;
    }
  }
}

.comment-content {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.comment-left {
  display: flex;
  flex: 1;
  align-items: flex-start;
}

.comment-avatar,
.reply-avatar {
  margin-right: 8px;
  flex-shrink: 0;
}

.comment-body,
.reply-body {
  flex: 1;
  min-width: 0;
}

.comment-header,
.reply-header {
  display: flex;
  align-items: baseline;
  margin-bottom: 2px;

  .username {
    color: #050505;
    font-size: 13px;
    font-weight: 600;
    line-height: 16px;
    margin-right: 6px;
  }

  .timestamp {
    color: #65676b;
    font-size: 12px;
    line-height: 16px;
    // cursor: pointer;

    // &:hover {
    //   text-decoration: underline;
    // }
  }
}

.comment-text,
.reply-text {
  color: #050505;
  font-size: 15px;
  line-height: 20px;
  margin-bottom: 4px;
  word-break: break-word;
}

.comment-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 4px;
}

.action-btn {
  background: none;
  border: none;
  color: #65676b;
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
  padding: 0;
  display: flex;
  align-items: center;
  gap: 4px;

  // &:hover {}

  &.reply-btn:hover {
    text-decoration: underline;
    color: #1877f2;
  }

  // &.emoji-btn:hover {}
}

.comment-actions-hover {
  display: flex;
  align-items: center;
  gap: 4px;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.2s ease, visibility 0.2s ease;

  &.visible {
    opacity: 1;
    visibility: visible;
  }
}

.hover-action-btn {
  background-color: #f2f3f5;
  border-radius: 6px;
  min-width: 32px !important;
  width: 32px;
  height: 32px;

  &:hover {
    background-color: #e4e6ea !important;
  }
}

.replies-container {
  margin-left: 40px;
  margin-top: 8px;
  border-left: none;
}

.reply-item {
  margin-bottom: 8px;
  position: relative;
  border-radius: 6px;

  &:hover {
    background-color: #f2f3f5;
    .comment-actions-hover {
      opacity: 1;
      visibility: visible;
    }
  }
}

.reply-content {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 4px 0;
}

.reply-left {
  display: flex;
  flex: 1;
  align-items: flex-start;
}

.nested-reply-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-left: 36px;
  margin-top: 8px;
  margin-bottom: 8px;
}

// Reply form styles
.reply-form,
.new-comment-form {
  margin-top: 8px;
  margin-left: 40px;
}

.new-comment-form {
  margin-left: 0;
  margin-top: 16px;
}

.reply-form-content,
.new-comment-content {
  display: flex;
  align-items: flex-start;
}

.reply-input-wrapper,
.new-comment-input-wrapper {
  flex: 1;
}

// .new-comment-input-container {
.reply-input-container {
  border: 1px solid #1877f2;
  border-radius: 20px;
  padding: 8px 12px;
  background: #f0f2f5;
  margin-bottom: 8px;
}

.new-comment-textarea {
  ::v-deep .v-input__control {
    .v-input__slot {
      margin-bottom: 0 !important;
    }
    .v-text-field__details {
      display: none;
    }
  }
}
.reply-textarea {
  width: 100%;
  border: none;
  background: transparent;
  resize: none;
  outline: none;
  font-family: inherit;
  font-size: 15px;
  line-height: 20px;
  color: #050505;
  min-height: 20px;

  &::placeholder {
    color: #65676b;
  }
}

.reply-form-actions,
.new-comment-form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.form-btn {
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 15px;
  font-weight: 600;
  line-height: 20px;
  padding: 6px 12px;

  &.cancel-btn {
    background: none;
    color: #65676b;

    &:hover {
      background-color: #f2f3f5;
    }
  }

  &.reply-submit-btn {
    background-color: #1877f2;
    color: white;

    &:hover:not(:disabled) {
      background-color: #166fe5;
    }

    &:disabled {
      background-color: #e4e6ea;
      color: #bcc0c4;
      cursor: not-allowed;
    }
  }
}

.add-comment-btn {
  display: flex;
  align-items: center;
  width: 100%;
  background: #f0f2f5;
  border: none;
  border-radius: 20px;
  padding: 8px 12px;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #e4e6ea;
  }
}

.add-comment-text {
  color: #65676b;
  font-size: 15px;
  line-height: 20px;
}

.comments-toggle {
  display: flex;
  align-items: center;
  gap: 8px;

  // &:hover {
  //   text-decoration: underline;
  // }
}

.comments-content {
  animation: fadeIn 0.3s ease-in-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

// Responsive
@media (max-width: 600px) {
  .replies-container {
    margin-left: 28px;
  }

  .reply-form,
  .new-comment-form {
    margin-left: 28px;
  }

  .nested-reply-actions {
    margin-left: 28px;
  }
}

.video-skeleton {
  position: relative;
  width: 100%;
  height: 500px;
  border-radius: 8px;
  overflow: hidden;
  background-color: #000;
}

.skeleton-overlay {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  z-index: 2;
}

.video-hidden {
  opacity: 0;
  visibility: hidden;
  position: absolute;
  z-index: -1;
}

.video-error-container {
  width: 100%;
  height: 500px;
  background: #f5f5f5;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 1;
}
.error-content {
  max-width: 400px;
}
.plyr {
  border-radius: 8px;
  width: 100%;
  overflow: hidden;
  transition: opacity 0.3s ease;
}
</style>
